import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Brain,
  Send,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Search,
  Bot,
  User,
  AlertCircle,
  LogIn,
  Copy,
  Check,
  Home,
  ArrowDown,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import MarkdownRenderer from '../../components/ai/shared/MarkdownRenderer';
import QoreAIWelcome from '../../components/ai/shared/QoreAIWelcome';
import ContextChip from '../../components/ai/shared/ContextChip';
import useQoreAIChat from '../../hooks/useQoreAIChat';
import '../../components/ai/shared/QoreAI.css';
import './AITutoring.css';

const MessageBubble = ({ message, onCopy, copied, onRetry }) => {
  const { t } = useTranslation();
  const isUser = message.role === 'user';
  const isError = message.isError;

  const formatTime = (ts) =>
    new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  return (
    <div className={`ait-msg ${isUser ? 'ait-msg--user' : 'ait-msg--ai'} ${isError ? 'ait-msg--error' : ''}`}>
      {!isUser && (
        <div className="ait-msg-avatar">
          {isError ? <AlertCircle size={18} /> : <Bot size={18} />}
        </div>
      )}
      <div className="ait-msg-body">
        <div className={`ait-msg-bubble ${isUser ? 'ait-msg-bubble--user' : 'ait-msg-bubble--ai'}`}>
          {isUser ? (
            <p className="ait-msg-text">{message.content}</p>
          ) : (
            <MarkdownRenderer content={message.content} />
          )}
        </div>
        <div className="ait-msg-footer">
          <span className="ait-msg-time">{formatTime(message.timestamp)}</span>
          <div className="qai-msg-actions" style={{ opacity: 1 }}>
            {!isError && (
              <button type="button" className="qai-msg-action-btn" onClick={() => onCopy(message.id, message.content)}>
                {copied === message.id ? <Check size={12} /> : <Copy size={12} />}
                {copied === message.id ? t('aiTutoring.copied') : t('aiTutoring.copy')}
              </button>
            )}
            {isError && onRetry && (
              <button type="button" className="qai-msg-action-btn" onClick={onRetry}>
                {t('aiTutoring.retry')}
              </button>
            )}
          </div>
        </div>
      </div>
      {isUser && (
        <div className="ait-msg-avatar ait-msg-avatar--user">
          <User size={18} />
        </div>
      )}
    </div>
  );
};

const TypingDots = () => {
  const { t } = useTranslation();
  return (
    <div className="ait-msg ait-msg--ai" role="status" aria-label={t('aiTutoring.thinking')}>
      <div className="ait-msg-avatar"><Bot size={18} /></div>
      <div className="ait-msg-body">
        <div className="ait-typing-bubble">
          <span className="ait-typing-label">{t('aiTutoring.thinking')}</span>
          <span className="ait-dot" />
          <span className="ait-dot" />
          <span className="ait-dot" />
        </div>
      </div>
    </div>
  );
};

const Sidebar = ({ onNewChat, onClose, isMobile, search, onSearchChange }) => {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/conversations?filter=all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setConversations(data.conversations || []);
        }
      } catch {
        setConversations([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = conversations.filter((c) =>
    (c.title || '').toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (ds) => {
    const diff = (Date.now() - new Date(ds)) / 3600000;
    if (diff < 1) return t('aiTutoring.justNow');
    if (diff < 24) return `${Math.floor(diff)}h ago`;
    if (diff < 48) return t('aiTutoring.yesterday');
    return new Date(ds).toLocaleDateString();
  };

  return (
    <div className="ait-sidebar-inner">
      <div className="ait-sidebar-head">
        <span className="ait-sidebar-title">{t('aiTutoring.conversations')}</span>
        <div className="ait-sidebar-head-actions">
          <button type="button" className="ait-sidebar-icon-btn" onClick={onNewChat} title={t('aiTutoring.newConversation')}>
            <Plus size={18} />
          </button>
          {isMobile && (
            <button type="button" className="ait-sidebar-icon-btn" onClick={onClose}>
              <PanelLeftClose size={18} />
            </button>
          )}
        </div>
      </div>

      <div className="ait-sidebar-search">
        <Search size={15} className="ait-sidebar-search-icon" />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('aiTutoring.searchHistory')}
          aria-label={t('aiTutoring.searchHistory')}
        />
      </div>

      <div className="ait-sidebar-list">
        {loading ? (
          <div className="ait-sidebar-empty"><div className="ait-spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="ait-sidebar-empty">
            <p>{search ? t('aiTutoring.noSearchResults') : t('aiTutoring.noConversationsYet')}</p>
            <span>{t('aiTutoring.startChatting')}</span>
          </div>
        ) : (
          filtered.map((conv) => (
            <button key={conv._id} type="button" className="ait-sidebar-item" disabled title={t('aiTutoring.historySoon')}>
              <div className="ait-sidebar-item-title">{conv.title || t('aiTutoring.untitledChat')}</div>
              <div className="ait-sidebar-item-meta">
                {formatDate(conv.lastMessageAt || conv.updatedAt)}
                <span>·</span>
                {conv.metadata?.messageCount || 0} {t('aiTutoring.msgs')}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

const ChatInput = ({ onSend, disabled }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
    }
  }, [value]);

  const submit = (e) => {
    e?.preventDefault();
    if (!value.trim() || disabled) return;
    onSend(value.trim());
    setValue('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  return (
    <form onSubmit={submit} className="ait-input-form">
      <div className="ait-input-box">
        <textarea
          ref={textareaRef}
          className="ait-textarea"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={t('aiTutoring.messagePlaceholder')}
          disabled={disabled}
          rows={1}
          autoComplete="off"
        />
        <div className="ait-input-actions">
          <button
            type="submit"
            className={`ait-send-btn ${value.trim() ? 'ait-send-btn--active' : ''}`}
            disabled={!value.trim() || disabled}
            aria-label={t('aiTutoring.send')}
          >
            <Send size={18} />
          </button>
        </div>
      </div>
      <p className="ait-input-hint">{t('aiTutoring.inputHint')}</p>
    </form>
  );
};

const AITutoring = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const messagesEndRef = useRef(null);
  const chatRef = useRef(null);

  const {
    messages,
    isAITyping,
    sendMessage,
    newConversation,
    retryLast,
    context,
  } = useQoreAIChat();

  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    setIsLoggedIn(!!token);
    const mobile = window.innerWidth <= 900;
    setIsMobile(mobile);
    setSidebarOpen(!mobile);
    const onResize = () => {
      const m = window.innerWidth <= 900;
      setIsMobile(m);
      if (m) setSidebarOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!showScrollDown) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isAITyping, showScrollDown]);

  const onChatScroll = () => {
    const el = chatRef.current;
    if (!el) return;
    setShowScrollDown(el.scrollHeight - el.scrollTop - el.clientHeight > 100);
  };

  const handleCopy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      /* ignore */
    }
  };

  const hasMessages = messages.length > 0;

  if (!isLoggedIn) {
    return (
      <div className="ait-page ait-page--landing">
        <header className="ait-immersive-nav">
          <Link to="/" className="ait-nav-home"><Home size={16} /> {t('header.home')}</Link>
        </header>
        <div className="ait-landing">
          <div className="ait-landing-card">
            <div className="ait-landing-glow" />
            <div className="ait-landing-icon"><Brain size={48} strokeWidth={1.5} /></div>
            <h1 className="ait-landing-title">{t('aiTutoring.pageTitle')}</h1>
            <p className="ait-landing-desc">{t('aiTutoring.pageDesc')}</p>
            <div className="ait-landing-tags">
              {[t('aiTutoring.tagMultiProvider'), t('aiTutoring.tagCodeDebugging'), t('aiTutoring.tagConceptExplanations'), t('aiTutoring.tagVirtualLabs'), t('aiTutoring.tagCareerGuidance')].map((tag) => (
                <span key={tag} className="ait-tag">{tag}</span>
              ))}
            </div>
            <button type="button" className="ait-landing-cta" onClick={() => navigate('/login')}>
              <LogIn size={18} />
              {t('aiTutoring.loginCta')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ait-page ait-page--immersive">
      <header className="ait-immersive-nav">
        <div className="ait-immersive-nav__left">
          <Link to="/academy" className="ait-nav-home"><Home size={16} /></Link>
          <span className="ait-immersive-brand">
            <Brain size={18} />
            {t('aiTutoring.qoreAI')}
          </span>
          <ContextChip context={context} />
        </div>
        <div className="ait-immersive-nav__right">
          <button type="button" className="ait-nav-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
            <span className="ait-nav-btn-label">{t('aiTutoring.openHistory')}</span>
          </button>
          <button type="button" className="ait-nav-btn" onClick={newConversation}>
            <Plus size={16} />
            <span className="ait-nav-btn-label">{t('aiTutoring.newChat')}</span>
          </button>
        </div>
      </header>

      <div className="ait-layout">
        {isMobile && sidebarOpen && (
          <button
            type="button"
            className="ait-sidebar-backdrop"
            aria-label={t('aiTutoring.closeSidebar')}
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <aside
          className={[
            'ait-sidebar',
            sidebarOpen ? 'ait-sidebar--open' : 'ait-sidebar--closed',
            isMobile ? 'ait-sidebar--mobile' : '',
          ].filter(Boolean).join(' ')}
        >
          <Sidebar
            onNewChat={() => { newConversation(); if (isMobile) setSidebarOpen(false); }}
            onClose={() => setSidebarOpen(false)}
            isMobile={isMobile}
            search={sidebarSearch}
            onSearchChange={setSidebarSearch}
          />
        </aside>

        <div className="ait-main">
          <div
            className="ait-chat"
            ref={chatRef}
            onScroll={onChatScroll}
          >
            {!hasMessages ? (
              <QoreAIWelcome variant="page" onPrompt={sendMessage} />
            ) : (
              <div className="ait-messages">
                {messages.map((msg) => (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    onCopy={handleCopy}
                    copied={copiedId}
                    onRetry={msg.isError ? retryLast : undefined}
                  />
                ))}
                {isAITyping && <TypingDots />}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {showScrollDown && hasMessages && (
            <button
              type="button"
              className="ait-scroll-down"
              onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
              aria-label={t('aiTutoring.scrollToBottom')}
            >
              <ArrowDown size={18} />
            </button>
          )}

          <div className="ait-bottom">
            <ChatInput onSend={sendMessage} disabled={isAITyping} />
            <p className="ait-disclaimer">{t('aiTutoring.disclaimer')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITutoring;